import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { GooglePlayIcon } from '@/components/GooglePlayIcon/GooglePlayIcon'
import { LOGO_PUBLIC_SRC } from '@/config/branding'
import { appLinks } from '@/config/links'

export function Footer() {
  const { t } = useTranslation()
  const year = new Date().getFullYear()

  return (
    <footer
      style={{
        background: 'linear-gradient(180deg, #0D1B3E 0%, #060E22 100%)',
        color: 'white',
      }}
    >
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <img
                src={LOGO_PUBLIC_SRC}
                alt={t('common.brandAlt')}
                className="h-9 w-9 rounded-xl object-cover"
                width={36}
                height={36}
              />
              <span
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 900,
                  fontSize: '22px',
                  color: 'white',
                  letterSpacing: '-0.02em',
                }}
              >
                {t('common.appName')}
              </span>
            </div>
            <p
              style={{
                fontFamily: 'DM Sans, sans-serif',
                color: 'rgba(255,255,255,0.55)',
                fontSize: '14px',
                lineHeight: 1.7,
                maxWidth: '320px',
              }}
            >
              {t('footer.tagline')}
            </p>
            <div className="mt-6">
              <a
                href={appLinks.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-xl px-5 py-3 transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'white',
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.14)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)'
                }}
              >
                <GooglePlayIcon size={20} />
                <div>
                  <div style={{ fontSize: '9px', opacity: 0.6, lineHeight: 1, fontFamily: 'DM Sans' }}>
                    {t('common.getItOn')}
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: 700, fontFamily: 'DM Sans', lineHeight: 1.2 }}>
                    {t('common.googlePlay')}
                  </div>
                </div>
              </a>
            </div>
          </div>

          <div>
            <h4
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
                fontSize: '15px',
                color: 'white',
                marginBottom: '16px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/', label: t('nav.home') },
                { to: '/support', label: t('nav.support') },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.5)'
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 700,
                fontSize: '15px',
                color: 'white',
                marginBottom: '16px',
                letterSpacing: '-0.01em',
              }}
            >
              {t('footer.legal')}
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/privacy-policy', label: t('nav.privacy') },
                { to: '/terms-of-service', label: t('nav.terms') },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '14px',
                      color: 'rgba(255,255,255,0.5)',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.9)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'rgba(255,255,255,0.5)'
                    }}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>
                {t('footer.contactUs')}
              </p>
              <a
                href={`mailto:${t('common.contactEmail')}`}
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '13px',
                  color: 'rgba(26, 86, 255, 0.9)',
                  textDecoration: 'none',
                }}
              >
                {t('common.contactEmail')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.35)' }}>
              {t('footer.rights', { year })}
            </p>
            <p
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.25)',
                textAlign: 'center',
              }}
            >
              {t('footer.disclaimer')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}
